/**
 * useRecentlyViewedReports — Tracks and persists recently visited report pages.
 *
 * Storage: localStorage key 'tms-reports-recently-viewed'
 * Capacity: up to MAX_ITEMS entries, newest first, deduplicated by route path.
 */
import { computed, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import { useLocalStorage } from '@vueuse/core';
import i18next from 'i18next';

const MAX_ITEMS = 10;
const STORAGE_KEY = 'tms-reports-recently-viewed';

export type RecentlyViewedEntry = {
  route: string;
  reportNameKey: string;
  viewedAt: string; // ISO 8601 timestamp
};

// Module-level reactive store — shared across all composable consumers
const _stored = useLocalStorage<RecentlyViewedEntry[]>(STORAGE_KEY, []);

const recentlyViewed = computed<RecentlyViewedEntry[]>({
  get: () => (Array.isArray(_stored.value) ? _stored.value : []),
  set: (v) => {
    _stored.value = v;
  },
});

/**
 * Call this composable at the top level of a report view's <script setup>.
 * It reads route.path and route.meta.reportNameKey (set in router.ts),
 * records a visit on mount, deduplicates by route, and limits to MAX_ITEMS.
 */
export function useTrackReportVisit(): void {
  const route = useRoute();

  onMounted(() => {
    const path = route.path;
    const nameKey = (route.meta.reportNameKey as string | undefined) ?? '';

    if (!nameKey) return;

    const deduplicated = recentlyViewed.value.filter((item) => item.route !== path);
    recentlyViewed.value = [
      { route: path, reportNameKey: nameKey, viewedAt: new Date().toISOString() },
      ...deduplicated,
    ].slice(0, MAX_ITEMS);
  });
}

/**
 * Returns the reactive recently-viewed list for use in the landing page.
 */
export function useRecentlyViewedList() {
  return { recentlyViewed };
}

/**
 * Formats an ISO timestamp into a human-readable string.
 * - Relative portion (< 30 days): translated via existing notifications.time.*
 *   keys. NOTE: i18next.language is always the literal string 'en' in this app
 *   (translated content is loaded dynamically from the /:domain bundle, not
 *   switched via i18next's language mechanism), so Intl.RelativeTimeFormat
 *   cannot be used here — it would always render English regardless of the
 *   tenant's configured locale.
 * - Absolute fallback (≥ 30 days): always rendered as DD/MM/YYYY (fixed format;
 *   locale-specific date formatting is not supported at this time).
 */
export function formatViewedAt(isoString: string): string {
  const now = new Date();
  const date = new Date(isoString);
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60_000);
  const diffHours = Math.floor(diffMs / 3_600_000);
  const diffDays = Math.floor(diffMs / 86_400_000);

  if (diffMins < 1) return i18next.t('reports.justNow');
  if (diffMins < 60) return i18next.t('notifications.time.minutesAgo', { count: diffMins });
  if (diffHours < 24) return i18next.t('notifications.time.hoursAgo', { count: diffHours });
  if (diffDays < 30) return i18next.t('notifications.time.daysAgo', { count: diffDays });
  const dd = String(date.getDate()).padStart(2, '0');
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  return `${dd}/${mm}/${date.getFullYear()}`;
}
